import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import { DataRequestCreateRequest, Project, DataRequest, Dataset, DatasetColumnMetadata, User, RedirectTargetsResponse } from "../types/api";

// A per-column narrowing the requester can ask for. Range filters are offered
// for numeric columns that carry stats; category filters for columns with a
// known category list. Personal-data columns are never filterable — their
// values are pseudonymized, so filtering on them would leak the original.
interface ColumnFilter {
  columnName: string;
  type: "range" | "categories";
  min?: number;
  max?: number;
  selectedCategories?: string[];
}

interface DataRequestFormProps {
  onRequestCreated?: (request: DataRequest) => void;
  preselectedDataset?: Dataset | null;
  preselectedProjectId?: number;
  user?: User | null;
}

export const DataRequestForm: React.FC<DataRequestFormProps> = ({
  onRequestCreated,
  preselectedDataset,
  preselectedProjectId,
  user,
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [sourceType, setSourceType] = useState<"upload" | "dataset">(
    preselectedDataset ? "dataset" : "upload"
  );
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(
    preselectedDataset || null
  );
  // Arrived via a "Request access" link for one specific dataset — the
  // source type, dataset choice, and project are all fixed, not editable.
  const datasetLocked = !!preselectedDataset;

  const [formData, setFormData] = useState<DataRequestCreateRequest>({
    project_id: preselectedProjectId || preselectedDataset?.uploaded_for_project_id || 0,
    title: "",
    description: "",
    requested_fields: [],
  });
  const [loading, setLoading] = useState(false);
  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dataset picker (searchable combobox)
  const navigate = useNavigate();
  const [datasetSearch, setDatasetSearch] = useState("");
  const [datasetDropdownOpen, setDatasetDropdownOpen] = useState(false);
  const datasetPickerRef = useRef<HTMLDivElement>(null);

  // Advanced options. Collapsed by default: an untouched form requests the
  // whole file with no narrowing, which is what most requesters want. Opening
  // this section is what opts into column selection and filtering.
  const [showAdvanced, setShowAdvanced] = useState(false);
  // null = "all columns" (the default). A non-null set means the requester has
  // explicitly narrowed the request.
  const [selectedColumns, setSelectedColumns] = useState<string[] | null>(null);
  const [columnFilters, setColumnFilters] = useState<Map<string, ColumnFilter>>(new Map());
  const [requestedFieldsText, setRequestedFieldsText] = useState("");
  const [processingNotesText, setProcessingNotesText] = useState("");

  // Redirect targets for PI quick-forward
  const [redirectTargets, setRedirectTargets] = useState<RedirectTargetsResponse | null>(null);
  const [selectedRedirectUserIDs, setSelectedRedirectUserIDs] = useState<Set<number>>(new Set());
  const [selectedRedirectOrgIDs, setSelectedRedirectOrgIDs] = useState<Set<number>>(new Set());
  const [showRedirectSection, setShowRedirectSection] = useState(false);
  const [redirectNotes, setRedirectNotes] = useState("");

  useEffect(() => {
    loadProjects();
  }, []);

  // Auto-select project if only one exists and not already selected
  useEffect(() => {
    if (projects.length === 1 && formData.project_id === 0) {
      setFormData((prev) => ({
        ...prev,
        project_id: projects[0].id,
      }));
    }
  }, [projects, formData.project_id]);

  useEffect(() => {
    if (sourceType === "dataset") {
      loadDatasets();
    }
  }, [sourceType]);

  // Load redirect targets for PI quick-forward feature
  useEffect(() => {
    const loadRedirectTargets = async () => {
      try {
        const targets = await apiService.getRedirectTargets();
        setRedirectTargets(targets);
      } catch (err) {
        console.warn("Failed to load redirect targets:", err);
      }
    };

    // Only load if user is a PI (determined by having admin access)
    if (user && redirectTargets === null) {
      loadRedirectTargets();
    }
  }, [user, redirectTargets]);

  // Pre-populate form when dataset is preselected. project_id falls back to
  // the dataset's own owning project — DatasetBrowser's "Request access"
  // doesn't pass preselectedProjectId separately, only the dataset itself.
  useEffect(() => {
    if (preselectedDataset) {
      setSourceType("dataset");
      setSelectedDataset(preselectedDataset);
      setFormData((prev) => ({
        ...prev,
        project_id: prev.project_id || preselectedProjectId || preselectedDataset.uploaded_for_project_id || 0,
        title: prev.title || preselectedDataset.display_name,
        description: prev.description || `Request for data from ${preselectedDataset.display_name}`,
      }));
    }
  }, [preselectedDataset, preselectedProjectId]);

  // Close the dataset dropdown when clicking outside the picker
  useEffect(() => {
    if (!datasetDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (datasetPickerRef.current && !datasetPickerRef.current.contains(e.target as Node)) {
        setDatasetDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [datasetDropdownOpen]);

  const loadProjects = async () => {
    try {
      const projectList = await apiService.getProjects();
      setProjects(projectList);
    } catch (err) {
      setError("Failed to load projects");
    }
  };

  const loadDatasets = async () => {
    try {
      setLoadingDatasets(true);
      const response = await apiService.getDatasets("browsable");
      setDatasets(response.datasets ?? []);

      // If there's a preselected dataset, ensure it's in the list
      if (preselectedDataset) {
        const found = response.datasets.find(
          (ds) => ds.id === preselectedDataset.id &&
                  ds.data_service_id === preselectedDataset.data_service_id
        );
        if (found) {
          setSelectedDataset(found);
        }
      }
    } catch (err: any) {
      setError(err.message || "Failed to load datasets");
    } finally {
      setLoadingDatasets(false);
    }
  };

  const handleSourceTypeChange = (newSourceType: "upload" | "dataset") => {
    setSourceType(newSourceType);
  };

  const handleDatasetSelect = (dataset: Dataset) => {
    setSelectedDataset(dataset);
    // Column choices belong to the previous dataset — drop them.
    setSelectedColumns(null);
    setColumnFilters(new Map());

    // Pre-fill title and description
    setFormData((prev) => ({
      ...prev,
      title: prev.title || dataset.display_name,
      description: prev.description || `Request for data from ${dataset.display_name}`,
    }));
  };

  const allColumnNames = (selectedDataset?.column_metadata ?? []).map((c) => c.name);
  // Treated as "all columns" until the requester narrows them.
  const effectiveColumns = selectedColumns ?? allColumnNames;

  const isNumericalType = (type: string) =>
    type === "integer" || type === "float" || type === "number";
  const hasStats = (col: DatasetColumnMetadata) => col.stats !== undefined;
  const hasCategories = (col: DatasetColumnMetadata) =>
    !!col.categories && col.categories.length > 0;
  const isIdentifierColumn = (name: string) =>
    selectedDataset?.identifier_columns?.includes(name) || false;

  const handleColumnToggle = (columnName: string) => {
    setSelectedColumns((prev) => {
      const base = prev ?? allColumnNames;
      const next = base.includes(columnName)
        ? base.filter((c) => c !== columnName)
        : [...base, columnName];
      return next;
    });
    // Deselecting a column drops any filter that referenced it.
    setColumnFilters((prev) => {
      if (!prev.has(columnName)) return prev;
      const base = selectedColumns ?? allColumnNames;
      if (!base.includes(columnName)) return prev; // was just selected
      const next = new Map(prev);
      next.delete(columnName);
      return next;
    });
  };

  const handleFilterChange = (columnName: string, filter: ColumnFilter | null) => {
    setColumnFilters((prev) => {
      const next = new Map(prev);
      if (filter) next.set(columnName, filter);
      else next.delete(columnName);
      return next;
    });
    // Filtering a column implies wanting it, so make sure it's selected.
    if (filter) {
      setSelectedColumns((prev) => {
        const base = prev ?? allColumnNames;
        return base.includes(columnName) ? base : [...base, columnName];
      });
    }
  };

  // Free-text list: comma-separated or one per line.
  const parseList = (text: string): string[] =>
    text
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);

  // Machine-readable criteria the reviewer and data provider act on.
  const buildFilterCriteria = (): string[] => {
    const criteria: string[] = [];
    columnFilters.forEach((filter, columnName) => {
      if (filter.type === "range") {
        if (filter.min !== undefined) criteria.push(`${columnName} >= ${filter.min}`);
        if (filter.max !== undefined) criteria.push(`${columnName} <= ${filter.max}`);
      } else if (filter.selectedCategories?.length) {
        criteria.push(`${columnName} IN [${filter.selectedCategories.join(", ")}]`);
      }
    });
    return criteria;
  };

  const clearAllFilters = () => setColumnFilters(new Map());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let requestData: DataRequestCreateRequest;

      if (sourceType === "dataset") {
        // Dataset source validation
        if (!selectedDataset) {
          setError("Please select a dataset");
          setLoading(false);
          return;
        }

        if (effectiveColumns.length === 0) {
          setError("Select at least one column, or clear the column selection to request the whole file");
          setLoading(false);
          return;
        }

        // The backend requires selected_columns whenever dataset_id is set, so
        // an unnarrowed request sends the full column list.
        const criteria = buildFilterCriteria();

        requestData = {
          ...formData,
          dataset_id: selectedDataset.id,
          data_service_id: selectedDataset.data_service_id, // Required for multi-service orgs
          selected_columns: effectiveColumns,
          requested_fields: effectiveColumns, // For backward compatibility
          filter_criteria: criteria.length > 0 ? criteria : undefined,
          processing_notes: processingNotesText.trim() || undefined,
        };
      } else {
        // Upload source (dataset_id will be NULL). There is no column
        // metadata to pick from yet, so the requester names fields as text.
        requestData = {
          ...formData,
          requested_fields: parseList(requestedFieldsText),
          processing_notes: processingNotesText.trim() || undefined,
        };
      }

      const createdRequest = await apiService.createDataRequest(requestData);

      // If redirect targets are selected, immediately redirect the request
      if (selectedRedirectUserIDs.size > 0 || selectedRedirectOrgIDs.size > 0) {
        try {
          await apiService.redirectDataRequest(createdRequest.id, {
            to_user_ids: Array.from(selectedRedirectUserIDs),
            to_org_ids: Array.from(selectedRedirectOrgIDs),
            notes: redirectNotes || "Request created and forwarded to data provider",
          });
          setSuccess("Data request created and forwarded successfully!");
        } catch (redirectErr) {
          // Request was created but redirect failed - still show success but with warning
          console.error("Failed to redirect request:", redirectErr);
          setSuccess("Data request created, but failed to forward. You can forward it manually from the request details.");
        }
      } else {
        setSuccess("Data request created successfully!");
      }

      // Reset form
      setFormData({
        project_id: 0,
        title: "",
        description: "",
        requested_fields: [],
      });
      setSelectedDataset(null);
      setSourceType("upload");
      setShowAdvanced(false);
      setSelectedColumns(null);
      setColumnFilters(new Map());
      setRequestedFieldsText("");
      setProcessingNotesText("");
      setSelectedRedirectUserIDs(new Set());
      setSelectedRedirectOrgIDs(new Set());
      setShowRedirectSection(false);
      setRedirectNotes("");

      if (onRequestCreated) {
        onRequestCreated(createdRequest);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create data request",
      );
    } finally {
      setLoading(false);
    }
  };

  // Only datasets the user must request - server already filtered to what they
  // may see via metadata_scope; we further hide ones already downloadable.
  const datasetQuery = datasetSearch.trim().toLowerCase();
  const visibleDatasetOptions = datasets
    .filter((d) => !d.can_download)
    .filter(
      (d) =>
        !datasetQuery ||
        d.display_name.toLowerCase().includes(datasetQuery) ||
        (d.description || "").toLowerCase().includes(datasetQuery),
    )
    .sort((a, b) => {
      const aSel = selectedDataset?.id === a.id && selectedDataset?.data_service_id === a.data_service_id;
      const bSel = selectedDataset?.id === b.id && selectedDataset?.data_service_id === b.data_service_id;
      return aSel === bSel ? 0 : aSel ? -1 : 1;
    });

  return (
    <div style={{ maxWidth: "720px", margin: "0 auto", padding: "24px" }}>
      <style>{`
        .drf-card { background: var(--surface-2); border: 0.5px solid var(--border); border-radius: var(--radius); padding: 24px; }
        .drf-heading { font-size: 20px; font-weight: 700; color: var(--text-primary); margin: 0 0 20px; font-family: var(--font-display); }
        .drf-banner { margin-bottom: 16px; padding: 12px 16px; border-radius: var(--radius); font-size: 14px; }
        .drf-banner.error { background: var(--bg-danger); color: var(--text-danger); }
        .drf-banner.success { background: var(--bg-success); color: var(--text-success); }
        .drf-field { margin-bottom: 20px; }
        .drf-label { display: block; font-size: 13px; font-weight: 500; color: var(--text-secondary); margin-bottom: 6px; }
        .drf-input, .drf-select, .drf-textarea {
          width: 100%; padding: 9px 12px; font-size: 14px; font-family: inherit;
          border: 0.5px solid var(--border-strong); border-radius: var(--radius);
          background: var(--surface-2); color: var(--text-primary);
        }
        .drf-input:focus, .drf-select:focus, .drf-textarea:focus {
          outline: none; border-color: var(--border-accent);
        }
        .drf-select:disabled { background: var(--surface-1); cursor: default; }
        .drf-radio-group { display: flex; gap: 20px; }
        .drf-radio-label { display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 14px; color: var(--text-primary); }
        .drf-section { border: 0.5px solid var(--border); border-radius: var(--radius); padding: 16px; background: var(--surface-1); }
        .drf-section-title { font-size: 14px; font-weight: 600; color: var(--text-primary); margin: 0 0 12px; }
        .drf-picker { position: relative; }
        .drf-dropdown {
          position: absolute; z-index: 10; margin-top: 4px; width: 100%;
          background: var(--surface-2); border: 0.5px solid var(--border); border-radius: var(--radius);
          box-shadow: 0 4px 16px rgba(0,0,0,0.08); max-height: 280px; overflow-y: auto;
        }
        .drf-dropdown-empty { padding: 12px 16px; font-size: 13px; color: var(--text-muted); }
        .drf-dropdown-row {
          width: 100%; text-align: left; padding: 10px 14px; border: none; background: none;
          border-bottom: 0.5px solid var(--border); cursor: pointer; font-family: inherit;
        }
        .drf-dropdown-row:last-child { border-bottom: none; }
        .drf-dropdown-row:hover, .drf-dropdown-row.selected { background: var(--bg-accent-muted); }
        .drf-dropdown-row-title { font-size: 14px; font-weight: 500; color: var(--text-primary); }
        .drf-dropdown-row-meta { font-size: 12px; color: var(--text-muted); margin-top: 2px; }
        .drf-selected-dataset {
          margin-top: 10px; display: flex; align-items: center; justify-content: space-between;
          border: 1px solid var(--border-accent); background: var(--bg-accent-muted);
          border-radius: var(--radius); padding: 10px 14px;
        }
        .drf-selected-dataset-title { font-size: 14px; font-weight: 600; color: var(--text-primary); }
        .drf-selected-dataset-desc { font-size: 13px; color: var(--text-secondary); }
        .drf-link-btn { background: none; border: none; padding: 0; font-size: 13px; color: var(--text-accent); cursor: pointer; font-family: inherit; }
        .drf-link-btn:hover { text-decoration: underline; }
        .drf-collapse-toggle {
          display: flex; align-items: center; gap: 6px; background: none; border: none; padding: 0;
          font-size: 13px; font-weight: 500; color: var(--text-secondary); cursor: pointer; font-family: inherit;
        }
        .drf-collapse-toggle:hover { color: var(--text-accent); }
        .drf-collapse-icon { transition: transform 0.15s; }
        .drf-collapse-icon.open { transform: rotate(45deg); }
        .drf-checkbox-row { display: flex; align-items: center; gap: 10px; padding: 8px 10px; border-radius: var(--radius); cursor: pointer; }
        .drf-checkbox-row:hover { background: var(--surface-2); }
        .drf-submit-btn {
          width: 100%; padding: 12px; font-size: 14px; font-weight: 500; color: var(--on-primary);
          background: var(--fill-primary); border: none; border-radius: var(--radius); cursor: pointer;
        }
        .drf-submit-btn:disabled {
          background: var(--fill-disabled); color: var(--text-disabled); cursor: not-allowed;
        }
        .drf-advanced-hint { font-weight: 400; color: var(--text-muted); font-size: 12px; }
        .drf-advanced-body {
          margin-top: 12px; padding: 16px; display: flex; flex-direction: column; gap: 18px;
          border: 0.5px solid var(--border); border-radius: var(--radius); background: var(--surface-1);
        }
        .drf-advanced-note { margin: 6px 0 0; font-size: 12px; color: var(--text-muted); }
        .drf-advanced-head {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px; margin-bottom: 8px; flex-wrap: wrap;
        }
        .drf-advanced-head-actions { display: flex; gap: 12px; }
        .drf-count { font-weight: 400; color: var(--text-muted); margin-left: 6px; }
        .drf-col-table-wrap {
          border: 0.5px solid var(--border-strong); border-radius: var(--radius);
          background: var(--surface-2); max-height: 320px; overflow: auto;
        }
        .drf-col-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .drf-col-table th {
          position: sticky; top: 0; z-index: 1; background: var(--surface-1);
          text-align: left; font-size: 11px; font-weight: 500; color: var(--text-muted);
          text-transform: uppercase; letter-spacing: 0.04em;
          padding: 7px 10px; border-bottom: 0.5px solid var(--border);
        }
        .drf-col-table td {
          padding: 6px 10px; border-bottom: 0.5px solid var(--border); vertical-align: middle;
        }
        .drf-col-table tbody tr:last-child td { border-bottom: none; }
        .drf-col-table tbody tr.selected { background: var(--bg-accent-muted); }
        .drf-col-table input[type="checkbox"] { width: 14px; height: 14px; }
        .drf-col-name { font-weight: 500; color: var(--text-primary); }
        .drf-col-type { color: var(--text-muted); }
        .drf-col-nofilter { font-size: 12px; color: var(--text-muted); }
        .drf-pii-tag {
          margin-left: 6px; font-size: 10px; font-weight: 600; padding: 1px 5px;
          border-radius: 3px; background: var(--bg-warning); color: var(--text-warning);
          white-space: nowrap;
        }
        .drf-range { display: flex; align-items: center; gap: 5px; }
        .drf-range-input {
          width: 88px; padding: 4px 6px; font-size: 12px; font-family: inherit;
          border: 0.5px solid var(--border-strong); border-radius: 5px;
          background: var(--surface-2); color: var(--text-primary);
        }
        .drf-range-input:focus { outline: none; border-color: var(--border-accent); }
        .drf-range-dash { color: var(--text-muted); font-size: 12px; }
        .drf-cat-select {
          width: 100%; padding: 3px; font-size: 12px; font-family: inherit;
          border: 0.5px solid var(--border-strong); border-radius: 5px;
          background: var(--surface-2); color: var(--text-primary);
        }
      `}</style>

      <div className="drf-card">
        <h2 className="drf-heading">Create Data Request</h2>

        {error && <div className="drf-banner error">{error}</div>}
        {success && <div className="drf-banner success">{success}</div>}

        <form onSubmit={handleSubmit}>
          {/* Project Selection */}
          <div className="drf-field">
            <label htmlFor="project_id" className="drf-label">Project *</label>
            <select
              id="project_id"
              value={formData.project_id}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  project_id: parseInt(e.target.value),
                }))
              }
              required
              // Only actually lock this once a project was successfully
              // resolved from the dataset — never leave the user stuck with
              // a disabled selector stuck at "no project chosen."
              disabled={projects.length === 1 || (datasetLocked && formData.project_id !== 0)}
              className="drf-select"
            >
              {projects.length > 1 && <option value={0}>Select a project</option>}
              {projects &&
                projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.title}
                  </option>
                ))}
            </select>
          </div>

          {/* Source Type Selection — hidden when a specific dataset arrived via
              a "Request access" link; there's nothing to choose in that case. */}
          {!datasetLocked && (
            <div className="drf-field">
              <label className="drf-label">Data Source Type *</label>
              <div className="drf-radio-group">
                <label className="drf-radio-label">
                  <input
                    type="radio"
                    name="source_type"
                    value="upload"
                    checked={sourceType === "upload"}
                    onChange={(e) => handleSourceTypeChange(e.target.value as "upload" | "dataset")}
                  />
                  Request new dataset
                </label>
                <label className="drf-radio-label">
                  <input
                    type="radio"
                    name="source_type"
                    value="dataset"
                    checked={sourceType === "dataset"}
                    onChange={(e) => handleSourceTypeChange(e.target.value as "upload" | "dataset")}
                  />
                  Select from existing datasets
                </label>
              </div>
            </div>
          )}

          {/* Title */}
          <div className="drf-field">
            <label htmlFor="title" className="drf-label">Request Title *</label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, title: e.target.value }))
              }
              placeholder="e.g., Patient data for cardiovascular study"
              required
              className="drf-input"
            />
          </div>

          {/* Description */}
          <div className="drf-field">
            <label htmlFor="description" className="drf-label">Description *</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
              rows={4}
              required
              className="drf-textarea"
            />
          </div>

          {/* Dataset Selection (shown when source_type is "dataset") */}
          {sourceType === "dataset" && (
            <div className="drf-field drf-section">
              <p className="drf-section-title">{datasetLocked ? "Dataset" : "Select Dataset *"}</p>

              {datasetLocked ? (
                // Arrived via a "Request access" link for this exact dataset —
                // fixed, not a picker.
                selectedDataset && (
                  <div className="drf-selected-dataset" style={{ marginTop: 0 }}>
                    <div style={{ minWidth: 0 }}>
                      <div className="drf-selected-dataset-title">{selectedDataset.display_name}</div>
                      {selectedDataset.description && (
                        <div className="drf-selected-dataset-desc">{selectedDataset.description}</div>
                      )}
                    </div>
                  </div>
                )
              ) : loadingDatasets ? (
                <p style={{ fontSize: "13px", color: "var(--text-muted)" }}>Loading datasets…</p>
              ) : (
                <div>
                  {/* Searchable combobox — type to filter, scales to any catalog size */}
                  <div className="drf-picker" ref={datasetPickerRef}>
                    <input
                      type="text"
                      value={datasetSearch}
                      onChange={(e) => { setDatasetSearch(e.target.value); setDatasetDropdownOpen(true); }}
                      onFocus={() => setDatasetDropdownOpen(true)}
                      placeholder={selectedDataset ? `Selected: ${selectedDataset.display_name}` : "Search datasets by name…"}
                      className="drf-input"
                    />
                    {datasetDropdownOpen && (
                      <div className="drf-dropdown">
                        {visibleDatasetOptions.length === 0 ? (
                          <div className="drf-dropdown-empty">
                            {datasets.filter((d) => !d.can_download).length === 0
                              ? "No datasets available to request"
                              : "No datasets match your search"}
                          </div>
                        ) : (
                          visibleDatasetOptions.map((dataset) => {
                            const isSel = selectedDataset?.id === dataset.id && selectedDataset?.data_service_id === dataset.data_service_id;
                            return (
                              <button
                                type="button"
                                key={`${dataset.data_service_id}-${dataset.id}`}
                                onClick={() => { handleDatasetSelect(dataset); setDatasetSearch(""); setDatasetDropdownOpen(false); }}
                                className={`drf-dropdown-row ${isSel ? "selected" : ""}`}
                              >
                                <div className="drf-dropdown-row-title">{dataset.display_name}</div>
                                <div className="drf-dropdown-row-meta">
                                  {dataset.row_count?.toLocaleString() || "N/A"} rows
                                  {dataset.column_metadata && ` · ${dataset.column_metadata.length} columns`}
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>

                  {/* Confirmation of the chosen dataset */}
                  {selectedDataset && (
                    <div className="drf-selected-dataset">
                      <div style={{ minWidth: 0 }}>
                        <div className="drf-selected-dataset-title">{selectedDataset.display_name}</div>
                        {selectedDataset.description && (
                          <div className="drf-selected-dataset-desc">{selectedDataset.description}</div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedDataset(null)}
                        className="drf-link-btn"
                        style={{ color: "var(--text-muted)", flexShrink: 0, marginLeft: "12px" }}
                      >
                        Clear
                      </button>
                    </div>
                  )}

                  {/* Hand off to the dedicated catalog for full browsing/filtering */}
                  <div style={{ marginTop: "10px", fontSize: "13px" }}>
                    <button type="button" onClick={() => navigate("/datasets")} className="drf-link-btn">
                      Browse full catalog →
                    </button>
                    <span style={{ color: "var(--text-muted)", marginLeft: "8px" }}>
                      to explore datasets with filters
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Advanced options. Collapsed by default so the common case — "send
              me the whole file" — is a four-field form. Opening it is what
              opts into column selection and filtering. */}
          <div className="drf-field">
            <button
              type="button"
              className="drf-collapse-toggle"
              onClick={() => setShowAdvanced(!showAdvanced)}
              aria-expanded={showAdvanced}
            >
              <svg
                className={`drf-collapse-icon ${showAdvanced ? "open" : ""}`}
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              Advanced options
              {!showAdvanced && (
                <span className="drf-advanced-hint">
                  narrow to specific columns or value ranges
                </span>
              )}
            </button>

            {showAdvanced && (
              <div className="drf-advanced-body">
                {sourceType === "dataset" && !selectedDataset && (
                  <p className="drf-advanced-note">
                    Select a dataset above to choose columns and filters.
                  </p>
                )}

                {sourceType === "dataset" &&
                  selectedDataset &&
                  (selectedDataset.column_metadata?.length ?? 0) === 0 && (
                    <p className="drf-advanced-note">
                      This dataset has no column metadata, so it can only be
                      requested in full.
                    </p>
                  )}

                {/* Column selection + per-column filters */}
                {sourceType === "dataset" &&
                  selectedDataset &&
                  (selectedDataset.column_metadata?.length ?? 0) > 0 && (
                    <div>
                      <div className="drf-advanced-head">
                        <label className="drf-label" style={{ marginBottom: 0 }}>
                          Columns
                          <span className="drf-count">
                            {selectedColumns === null
                              ? `all ${allColumnNames.length}`
                              : `${selectedColumns.length} of ${allColumnNames.length}`}
                            {columnFilters.size > 0 &&
                              ` · ${columnFilters.size} filtered`}
                          </span>
                        </label>
                        <div className="drf-advanced-head-actions">
                          {selectedColumns !== null && (
                            <button
                              type="button"
                              className="drf-link-btn"
                              onClick={() => setSelectedColumns(null)}
                            >
                              Select all
                            </button>
                          )}
                          {(selectedColumns === null ||
                            selectedColumns.length > 0) && (
                            <button
                              type="button"
                              className="drf-link-btn"
                              onClick={() => setSelectedColumns([])}
                            >
                              Clear
                            </button>
                          )}
                          {columnFilters.size > 0 && (
                            <button
                              type="button"
                              className="drf-link-btn"
                              onClick={clearAllFilters}
                            >
                              Clear filters
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="drf-col-table-wrap">
                        <table className="drf-col-table">
                          <thead>
                            <tr>
                              <th style={{ width: "34px" }} />
                              <th>Column</th>
                              <th style={{ width: "84px" }}>Type</th>
                              <th style={{ width: "230px" }}>Filter</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedDataset.column_metadata!.map((col) => {
                              const isSelected = effectiveColumns.includes(col.name);
                              const filter = columnFilters.get(col.name);
                              return (
                                <tr
                                  key={col.name}
                                  className={isSelected ? "selected" : ""}
                                >
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => handleColumnToggle(col.name)}
                                      aria-label={`Include ${col.name}`}
                                    />
                                  </td>
                                  <td>
                                    <span className="drf-col-name">{col.name}</span>
                                    {col.is_pii && (
                                      <span
                                        className="drf-pii-tag"
                                        title="Personal data — values are pseudonymized"
                                      >
                                        Personal data
                                      </span>
                                    )}
                                    {!col.is_pii && isIdentifierColumn(col.name) && (
                                      <span
                                        className="drf-pii-tag"
                                        title="Identifier column — will be de-identified"
                                      >
                                        Identifier
                                      </span>
                                    )}
                                  </td>
                                  <td className="drf-col-type">{col.type}</td>
                                  <td>
                                    {col.is_pii ? (
                                      <span
                                        className="drf-col-nofilter"
                                        title="Personal-data columns are pseudonymized, so their original values cannot be filtered on"
                                      >
                                        Not filterable
                                      </span>
                                    ) : hasStats(col) && isNumericalType(col.type) ? (
                                      <div className="drf-range">
                                        <input
                                          type="number"
                                          className="drf-range-input"
                                          placeholder={String(col.stats!.min)}
                                          aria-label={`Minimum ${col.name}`}
                                          value={filter?.min ?? ""}
                                          step={col.type === "float" ? "0.1" : "1"}
                                          title={`Range ${col.stats!.min} – ${col.stats!.max}`}
                                          onChange={(e) => {
                                            const v =
                                              e.target.value === ""
                                                ? undefined
                                                : parseFloat(e.target.value);
                                            const cur = columnFilters.get(col.name);
                                            if (v === undefined && cur?.max === undefined) {
                                              handleFilterChange(col.name, null);
                                            } else {
                                              handleFilterChange(col.name, {
                                                columnName: col.name,
                                                type: "range",
                                                min: v,
                                                max: cur?.max,
                                              });
                                            }
                                          }}
                                        />
                                        <span className="drf-range-dash">–</span>
                                        <input
                                          type="number"
                                          className="drf-range-input"
                                          placeholder={String(col.stats!.max)}
                                          aria-label={`Maximum ${col.name}`}
                                          value={filter?.max ?? ""}
                                          step={col.type === "float" ? "0.1" : "1"}
                                          title={`Range ${col.stats!.min} – ${col.stats!.max}`}
                                          onChange={(e) => {
                                            const v =
                                              e.target.value === ""
                                                ? undefined
                                                : parseFloat(e.target.value);
                                            const cur = columnFilters.get(col.name);
                                            if (v === undefined && cur?.min === undefined) {
                                              handleFilterChange(col.name, null);
                                            } else {
                                              handleFilterChange(col.name, {
                                                columnName: col.name,
                                                type: "range",
                                                min: cur?.min,
                                                max: v,
                                              });
                                            }
                                          }}
                                        />
                                      </div>
                                    ) : hasCategories(col) ? (
                                      <select
                                        className="drf-cat-select"
                                        multiple
                                        size={Math.min(3, col.categories!.length)}
                                        aria-label={`Categories for ${col.name}`}
                                        value={filter?.selectedCategories ?? []}
                                        onChange={(e) => {
                                          const picked = Array.from(
                                            e.target.selectedOptions,
                                          ).map((o) => o.value);
                                          handleFilterChange(
                                            col.name,
                                            picked.length === 0
                                              ? null
                                              : {
                                                  columnName: col.name,
                                                  type: "categories",
                                                  selectedCategories: picked,
                                                },
                                          );
                                        }}
                                      >
                                        {col.categories!.map((c) => (
                                          <option key={c} value={c}>
                                            {c}
                                          </option>
                                        ))}
                                      </select>
                                    ) : (
                                      <span className="drf-col-nofilter">—</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                {/* Upload path: no column metadata exists yet, so fields are named as text */}
                {sourceType === "upload" && (
                  <div>
                    <label htmlFor="requested_fields" className="drf-label">
                      Requested fields (optional)
                    </label>
                    <textarea
                      id="requested_fields"
                      className="drf-textarea"
                      rows={3}
                      value={requestedFieldsText}
                      onChange={(e) => setRequestedFieldsText(e.target.value)}
                      placeholder={"kennitala, first_name, last_name\nor one per line"}
                    />
                    <p className="drf-advanced-note">
                      Leave blank to request every field in the file.
                    </p>
                  </div>
                )}

                <div>
                  <label htmlFor="processing_notes" className="drf-label">
                    Notes for the data provider (optional)
                  </label>
                  <textarea
                    id="processing_notes"
                    className="drf-textarea"
                    rows={3}
                    value={processingNotesText}
                    onChange={(e) => setProcessingNotesText(e.target.value)}
                    placeholder={
                      sourceType === "dataset"
                        ? "Anything the reviewer should know — e.g. only adults, or flag that the age column has mixed units"
                        : "Filter criteria in your own words, or data quality issues to watch for"
                    }
                  />
                </div>
              </div>
            )}
          </div>

          {/* Forward to data provider — only meaningful when the data is NOT yet in
              the catalog (the "upload later" path). For an existing dataset there is
              nothing to forward: the PI approves and it auto-fulfills directly. */}
          {sourceType === "upload" && redirectTargets && (redirectTargets.data_uploaders?.length || 0) + (redirectTargets.organizations?.length || 0) > 0 && (
            <div className="drf-field drf-section">
              <button
                type="button"
                onClick={() => setShowRedirectSection(!showRedirectSection)}
                className="drf-collapse-toggle"
                style={{ width: "100%", justifyContent: "space-between" }}
              >
                <span>Optionally forward to data provider on creation</span>
                <span>{showRedirectSection ? "▼" : "▶"}</span>
              </button>

              {showRedirectSection && (
                <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "12px" }}>
                  <p style={{ fontSize: "13px", color: "var(--text-secondary)", margin: 0 }}>
                    Select data providers to forward this request to immediately after creation:
                  </p>

                  {/* Data Uploaders */}
                  {redirectTargets.data_uploaders && redirectTargets.data_uploaders.length > 0 && (
                    <div>
                      <p className="drf-label">Data Providers</p>
                      <div style={{ maxHeight: "160px", overflowY: "auto" }}>
                        {redirectTargets.data_uploaders.map((uploader) => (
                          <label key={uploader.id} className="drf-checkbox-row">
                            <input
                              type="checkbox"
                              checked={selectedRedirectUserIDs.has(uploader.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedRedirectUserIDs(prev => new Set(prev).add(uploader.id));
                                } else {
                                  setSelectedRedirectUserIDs(prev => {
                                    const newSet = new Set(prev);
                                    newSet.delete(uploader.id);
                                    return newSet;
                                  });
                                }
                              }}
                            />
                            <span style={{ fontSize: "13px", color: "var(--text-primary)" }}>
                              {uploader.name}
                              {uploader.organization_name && <span style={{ color: "var(--text-muted)" }}> ({uploader.organization_name})</span>}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Organizations */}
                  {redirectTargets.organizations && redirectTargets.organizations.length > 0 && (
                    <div>
                      <p className="drf-label">Organizations</p>
                      <div style={{ maxHeight: "160px", overflowY: "auto" }}>
                        {redirectTargets.organizations.map((org) => (
                          <label key={org.id} className="drf-checkbox-row">
                            <input
                              type="checkbox"
                              checked={selectedRedirectOrgIDs.has(org.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedRedirectOrgIDs(prev => new Set(prev).add(org.id));
                                } else {
                                  setSelectedRedirectOrgIDs(prev => {
                                    const newSet = new Set(prev);
                                    newSet.delete(org.id);
                                    return newSet;
                                  });
                                }
                              }}
                            />
                            <span style={{ fontSize: "13px", color: "var(--text-primary)" }}>
                              {org.name}
                              {org.short_name && <span style={{ color: "var(--text-muted)" }}> ({org.short_name})</span>}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Forward Notes */}
                  {(selectedRedirectUserIDs.size > 0 || selectedRedirectOrgIDs.size > 0) && (
                    <div>
                      <label className="drf-label">Forwarding note (optional)</label>
                      <textarea
                        value={redirectNotes}
                        onChange={(e) => setRedirectNotes(e.target.value)}
                        placeholder="e.g., 'Patient records for Q4 study'"
                        className="drf-textarea"
                        rows={2}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || formData.project_id === 0}
            className="drf-submit-btn"
            style={{ marginTop: "8px" }}
          >
            {loading ? "Submitting Request..." : "Submit Data Request"}
          </button>
        </form>
      </div>
    </div>
  );
};
